<template>
  <div class="quick-add">
    <template v-if="product">
      <div class="quick-add__content-wrapper">
        <div
          class="quick-add__content"
        >
          <div
            v-if="badge"
            class="quick-add__badge"
            v-text="badge"
          />

          <a
            class="quick-add__title"
            :href="getProductUrl"
            v-text="getProductTitle"
          />

          <product-prices
            :available="product?.available"
            block-class="quick-add"
            :compare-at-price="product?.compareAtPrice?.amount"
            :price="product?.price?.amount"
            :price-first="false"
            element="div"
          />

          <main-product-swatches
            v-if="swatches.length"
            :label="$string('product.form.color')"
            :product="product"
            :swatches="isActive ? swatches : []"
            :show-all="false"
            :limit-override="3"
            @swatch-updated="handleSwatchUpdate"
            @swatch-loaded="swatchLoaded"
            @swatches-ready="swatchesReady"
          />

          <div
            v-if="swatchLoading"
            class="main-product-swatches main-product__form liquid"
          >
            <div class="product-form__header">
              <div
                class="product-form__title"
                v-text="$string('product.form.color')"
              />
            </div>

            <div class="form-group">
              <div class="form-group__field">
                <label
                  class="
                    product-form__label product-form__label--swatch
                    product-form__label--image-loaded
                  "
                />
              </div>
            </div>
          </div>

          <product-form
            :product="product"
            has-size-guide
            has-size-swatches
            :variant="variant"
            :show-model-size="false"
            :shop-the-look="false"
            @update-variant="handleVariantUpdate"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="quick-add__content-wrapper">
        <div class="quick-add__content">
          <div class="quick-add__badge" />
          <h3 class="quick-add__title" />
          <div class="quick-add__prices">
            <div class="quick-add__price" />
          </div>
          <div class="quick-add__swatches">
            <div class="quick-add__swatch" />
          </div>
          <div class="product-form">
            <div class="form-group__field" />
            <div class="form-group__footer">
              <btn
                :label="$string('product.cta.add_to_cart')"
                modifiers="add-to-cart block center"
                :show-icon="false"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Quick add (quick-add)
 * -----------------------------------------------------------------------------
 * Display a quick add form in a product card.
 * @param {Boolean|String} handle - Handle for active product. or false for none
 *
 */
import { nextTick } from 'vue'
import { mapGetters } from 'vuex'
import productWithMetafields from '~/graphql/queries/productWithMetafields.gql'
import productsWithMetafields from '~/graphql/queries/productsWithMetafields.gql'
import { formatProduct } from '~/helpers/format'
import { handleize } from '~/helpers/general'
import MainProductSwatches from '~async/main-product/main-product-swatches/main-product-swatches'
import ProductForm from '~async/product-form/product-form'
import Btn from '~global/btn/btn'
import ProductPrices from '~global/product-prices/product-prices'

export default {
  name: 'QuickAdd',

  components: {
    Btn,
    MainProductSwatches,
    ProductForm,
    ProductPrices,
  },

  props: {
    handle: {
      type: [Boolean, String],
      default: '',
    },
  },

  emits: {
    'quick-add-close': () => true,
  },

  data() {
    return {
      state: 'loading',
      product: false,
      swatchProducts: [],
      swatches: [],
      variant: false,
      source: false,
      swatchLoading: true,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute if quick add is active
     */
    isActive() {
      return this.getActiveOverlay === 'quickAdd'
    },

    /**
     * Compute products master title.
     * @returns {String}
     */
    masterTitle() {
      if (!this.product) {
        return false
      }

      return this.product.title.split(' - ')[0].trim()
    },

    /**
     * Compute product title.
     * @returns {String}
     */
    getProductTitle() {
      if (!this.product) {
        return ''
      }

      let title = this.product.title

      if (title.includes(' - ')) {
        title = title.split(' - ')[0]
      }

      return title
    },

    /**
     * Compute product url.
     * @returns {String}
     */
    getProductUrl() {
      if (!this.product) {
        return ''
      }

      return this.product.url
    },

    /**
     * Compute gender based on product tag.
     * @returns {String}
     */
    gender() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => data.includes('gender:'))

      return tag
    },

    /**
     * Compute loading class depending on state.
     * @returns {String}
     */
    loadingClass() {
      return this.state === 'loading' && 'is-loading'
    },

    /**
     * Compute badge based on product tag.
     * @returns {String}
     */
    badge() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => data.includes('badge:'))

      if (!tag) {
        return false
      }

      return tag.split('badge:')[1]
    },
  },

  watch: {

    /**
     * Watch product handle change
     * @param {String} value - product handle
     */
    handle(value) {
      this.state = 'loading'
      this.queryProduct(value)
    },

    /**
     * Watch for updated swatch products.
     */
    swatchProducts() {
      this.getSwatchData()
    },
  },

  mounted() {
    cnvs.EventBus.listen('quick-add:update-handle', (response) => {
      this.queryProduct(response.handle)
      this.source = response.source
    })
  },

  methods: {

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle) {
      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productWithMetafields,
          variables: {
            country: cnvs.store.country,
            handle,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        const product = formatProduct(response)
        this.setDefaultVariant(product)
        this.product = product
        this.querySwatchProducts()

      } catch (error) {
        throw new Error('Failed to load product', error)
      }
    },

    /**
     * Load products from GraphQL and sort currently active product to front.
     */
    async querySwatchProducts() {
      let query = `(title:'${this.masterTitle}'*)`

      if (this.gender) {
        query += ` AND (tag:'${this.gender}')`
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productsWithMetafields,
          variables: {
            country: cnvs.store.country,
            query,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (!response.products.edges.length) {
          return
        }

        this.handleSwatchProductsQuerySuccess(response)

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Callback after successful query for associated products.
     * @param {Object} response - Response from GraphQl.
     */
    handleSwatchProductsQuerySuccess(response) {
      const tmpSwatches = []

      response.products.edges.forEach((product) => {
        const data = { product: product.node }

        if (this.isProductMatch(data.product.title)) {
          tmpSwatches.push(formatProduct(data))
        }
      })

      const activeProduct = tmpSwatches.find((product) => {
        return this.product.id === product.id
      })

      const index = tmpSwatches.indexOf(activeProduct)

      tmpSwatches.splice(index, 1)
      tmpSwatches.unshift(activeProduct)

      this.swatchProducts = tmpSwatches
    },

    /**
     * Set first available variant as the default
     * @param {Object} activeProduct - product
     */
    setDefaultVariant(activeProduct) {
      const currenVariant = activeProduct.variants.find((variant) => {
        return variant.available
      })

      this.handleVariantUpdate(currenVariant)
    },

    /**
     * Get list of all relevant swatch images.
     * sorted by Product ID.
     */
    getSwatchData() {
      this.swatches = this.swatchProducts.map((product) => {
        const color = this.checkForColorOption(product)

        if (!color) {
          return false
        }

        const {
          available,
          compareAtPrice,
          handle,
          id,
          tags,
          price,
        } = product

        const swatchSrc = this.formatSwatchSrc(color, tags)

        const isSale = compareAtPrice.amount && compareAtPrice.amount > price.amount

        return {
          available,
          handle,
          id,
          image: swatchSrc,
          name: color.values[0],
          sale: isSale,
        }
      })

      this.state = 'ready'
    },

    /**
     * Format swatchSrc.
     * @param {Object} color - Product color option.
     * @param {Array} tags - Product tags.
     * @returns {String}
     */
    formatSwatchSrc(color, tags) {
      const location = window.cnvs.urls.files.split('placeholder')[0]
      const key = handleize(color.values[0])
      const fabricTag = tags.find((tag) => tag.toLowerCase().includes('fabric'))
      let swatchSrc = `${location}color`

      if (fabricTag) {
        const fabric = fabricTag.split(':')[1]
          .split(' ')
          .join('-')
          .toLowerCase()
          .trim()

        swatchSrc = `${swatchSrc}-${fabric}`
      }

      swatchSrc = `${swatchSrc}-${key}_32x10.png`

      return swatchSrc
    },

    /**
     * Check product to see if it has color as an option.
     * @param {Object} product - Product object.
     * @returns {Object}
     */
    checkForColorOption(product) {
      if (!product) {
        return null
      }

      const color = product.options.find((option) => option.name.toLowerCase() === 'color')

      return color
    },

    /**
     * Determine if queried product matches current product.
     * @param {String}
     * @returns {Boolean}
     */
    isProductMatch(title) {
      const masterTitle = this.masterTitle.toLowerCase().trim()
      const newTitle = title.toLowerCase().split(' - ')[0].trim()

      return masterTitle === newTitle
    },

    /**
     * Add failed attribute to swatch images that 404.
     * @param {Object} swatch
     */
    swatchLoaded(swatch) {
      swatch.loaded = true
    },

    /**
     * Hides swatch loading state.
     */
    swatchesReady() {
      this.swatchLoading = false
    },

    /**
     * Swap active product with selected swatch product.
     * @param {Object} data
     */
    handleSwatchUpdate(data) {
      const product = this.swatchProducts.find((variant) => variant.id === data.id)

      if (!product) {
        return
      }

      const currentSize = this.variant.selectedOptions.find((option) => {
        return option.name === 'Size' || option.name === 'size'
      })

      if (currentSize) {
        const newVariant = product.variants.find((variant) => {
          const size = variant.selectedOptions.find((item) => {
            return item.name === 'Size' || item.name === 'size'
          })

          return size.value === currentSize.value && variant.available
        }) || product.variants.find((item) => {
          return item.available
        })

        if (newVariant) {
          this.handleVariantUpdate(newVariant)
        }
      }

      cnvs.EventBus.emit('activateSwatch', {
        swatch: data,
        source: this.source,
      })

      nextTick().then(() => {
        this.product = product
      })

    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.variant = variant
    },
  },
}
</script>

<style lang="scss">
@import './quick-add';
</style>
